import axios, { AxiosInstance, AxiosError } from 'axios'
import type {
  TravelApplication,
  TravelApplicationResponse,
  ApiResponse,
  BudgetCheckResponse,
  ApprovalPreviewResponse,
  EmployeeDTO,
  DepartmentDTO,
  CityDTO,
} from '@/types'
import type {
  CreateDraftRequest,
  CreateDraftResponseData,
  SubmitApplicationRequest,
  SubmitApplicationResponseData,
  UpdateDraftRequest,
  UpdateDraftResponse,
  SearchTravelApplicationRequest,
  SearchTravelApplicationResponse,
  OccupiedPeriod,
  CheckSchedulingResponse,
  CalculateHoursRequest,
  CalculateHoursResponse,
} from '@/types/travel'

/**
 * API 错误响应结构（嵌套 GlobalResponseAdvice 包装）
 */
interface ApiErrorData {
  code?: number
  message?: string
  data?: {
    message?: string
  }
}

/**
 * API 客户端
 * 与后端 API 端点交互
 */
class ApiClient {
  private instance: AxiosInstance

  constructor() {
    this.instance = axios.create({
      baseURL: '/api',
      timeout: 50000,
      headers: {
        'Content-Type': 'application/json',
        // CAS：告知服务端对 AJAX 请求返回 JSON(status:700) 而非 302 重定向
        'X-Cas-Redirect-Switch': 'disable',
      },
    })

    // 响应拦截器
    this.instance.interceptors.response.use(
      (response) => {
        const data = response.data
        // CAS 未登录：返回 {status:700, location:"cas-login-url"}，重定向到登录页
        if (data?.status === 700 || data?.code === 700) {
          const loginUrl = data.location || '/'
          console.warn('[CAS] 登录态已过期，跳转至登录页:', loginUrl)
          // 将 service 参数固定替换为 /api/cas/callback：
          //   - 后端 cas.app（serverName）与 cas.app.service（service）不能同时设置
          //   - 故后端无法通过 cas.app.service 设定固定回调地址
          //   - 由前端在跳转前统一替换，确保 CAS ticket 回到 Java 后端处理
          //     （APISIX 将 /api/* 路由到 Java），由 CasCallbackController 验票后重定向至 /dashboard
          //   - window.location.origin 自动适配 dev/test/prod 域名，无需硬编码
          try {
            const url = new URL(loginUrl)
            url.searchParams.set('service', `${window.location.origin}/api/cas/callback`)
            window.location.href = url.toString()
          } catch {
            window.location.href = loginUrl
          }
          return Promise.reject(new Error('登录态已过期，请重新登录'))
        }
        return data
      },
      (error: AxiosError<ApiErrorData>) => {
        // 优先从嵌套的 data.data.message 中提取错误消息（GlobalResponseAdvice 包装的情况）
        // 如果不存在，则使用 data.message（标准 ApiResponse）
        // 最后使用 error.message（网络错误等）
        const responseData = error.response?.data
        const nestedMessage = responseData?.data?.message
        const standardMessage = responseData?.message
        const status = error.response?.status
        const fallbackMessage = status === 500
          ? '服务器刚刚打了个喷嚏，我们已记录问题，请稍后再试~'
          : (error.message || '请求失败')
        const message = nestedMessage || standardMessage || fallbackMessage
        console.error('API 错误:', message)
        return Promise.reject(new Error(message))
      }
    )
  }

  /**
   * 更新草稿（自动保存）
   *
   * PUT /api/v1/applications/{id}
   */
  async updateDraft(id: number, data: UpdateDraftRequest): Promise<ApiResponse<UpdateDraftResponse>> {
    return this.instance.put(`/v1/applications/${id}`, data)
  }

  /**
   * 创建草稿申请
   */
  async createDraft(data: CreateDraftRequest): Promise<ApiResponse<CreateDraftResponseData>> {
    return this.instance.post('/v1/applications/draft', data)
  }

  /**
   * 提交差旅申请（支持自动保存）
   * 可选传入表单数据，后端会自动保存后再提交
   */
  async submitTravelApplication(
    id: string,
    formData?: SubmitApplicationRequest
  ): Promise<ApiResponse<SubmitApplicationResponseData>> {
    return this.instance.post(`/v1/applications/${id}/submit`, formData)
  }

  /**
   * 获取差旅申请详情
   */
  async getTravelApplication(id: string): Promise<ApiResponse<TravelApplication>> {
    return this.instance.get(`/v1/applications/${id}`)
  }

  /**
   * 检查预算
   */
  async checkBudget(applicantId: string, amount: number): Promise<ApiResponse<BudgetCheckResponse>> {
    return this.instance.post('/budget-check', {
      applicantId,
      amount,
    })
  }

  /**
   * 获取审批预览（调用百川 API 预测审批流程）
   *
   * 注意：POST 请求，对应后端 /api/v1/applications/{id}/preview-approval
   */
  async getApprovalPreview(
    id: string,
    companions?: Array<{ employeeId: string; name: string }>,
    applicantId?: string,
    applicantName?: string,
    startDate?: string,
    endDate?: string
  ): Promise<ApiResponse<ApprovalPreviewResponse>> {
    return this.instance.post(`/v1/applications/${id}/preview-approval`, {
      companions,
      applicantId,
      applicantName,
      startDate: startDate || undefined,
      endDate: endDate || undefined,
    })
  }

  /**
   * 撤回差旅申请
   *
   * 仅当申请处于 APPROVING 状态时可撤回
   */
  async withdrawApplication(id: string): Promise<ApiResponse<TravelApplicationResponse>> {
    return this.instance.post(`/v1/applications/${id}/withdraw`)
  }

  /**
   * 重新编辑已拒绝或已撤回的申请
   *
   * 将申请状态从 REJECTED/WITHDRAWN 改回 DRAFT
   */
  async reEditApplication(id: string): Promise<ApiResponse<TravelApplicationResponse>> {
    return this.instance.post(`/v1/applications/${id}/re-edit`)
  }

  /**
   * 查询流程链状态
   *
   * 获取已启动流程的当前审批进度
   */
  async getProcessChain(id: string): Promise<ApiResponse<ApprovalPreviewResponse>> {
    return this.instance.get(`/v1/applications/${id}/process-chain`)
  }

  /**
   * 删除差旅申请
   */
  async deleteTravelApplication(id: string): Promise<ApiResponse<void>> {
    return this.instance.delete(`/v1/applications/${id}`)
  }

  /**
   * 搜索差旅申请（列表页，支持分页、状态筛选、姓名搜索）
   *
   * POST /api/v1/applications/search
   */
  async searchApplications(
    params: SearchTravelApplicationRequest
  ): Promise<ApiResponse<SearchTravelApplicationResponse>> {
    return this.instance.post('/v1/applications/search', params)
  }

  /**
   * 从响应中提取 data 字段
   * 因为响应拦截器已经返回 response.data（即 {code, message, data}），
   * 需要再取一层 .data 才能拿到实际的业务数据
   */
  private extractData<T>(response: ApiResponse<T>): T {
    return response.data
  }

  /**
   * 搜索员工（返回完整的员工 DTO）
   */
  async searchEmployees(keyword: string): Promise<EmployeeDTO[]> {
    const response: ApiResponse<EmployeeDTO[]> = await this.instance.get('/v1/medusa/employees', {
      params: { q: keyword },
    })
    return this.extractData(response) || []
  }

  /**
   * 搜索部门（返回完整的部门 DTO）。
   * collection：主体/集合ID，传入则仅返回该主体下部门（不允许跨主体选择）。
   */
  async searchDepartments(keyword: string, collection?: string): Promise<DepartmentDTO[]> {
    const params: { q: string; collection?: string } = { q: keyword }
    if (collection) params.collection = collection
    const response: ApiResponse<DepartmentDTO[]> = await this.instance.get('/v1/medusa/departments', {
      params,
    })
    return this.extractData(response) || []
  }

  /**
   * 获取无差旅预算的部门编号列表（Apollo 配置）。
   * 用于前端：员工自动回填跳过黑名单部门、部门搜索下拉灰显与点击拦截。
   * 失败由调用方降级为空数组。
   */
  async getNoBudgetDepartments(): Promise<string[]> {
    const response: ApiResponse<string[]> = await this.instance.get('/v1/config/no-budget-departments')
    return this.extractData(response) || []
  }

  /**
   * 获取热门城市列表（默认城市）
   */
  async getHotCities(): Promise<CityDTO[]> {
    const response: ApiResponse<CityDTO[]> = await this.instance.get('/v1/medusa/cities')
    return this.extractData(response) || []
  }

  /**
   * 搜索城市（支持中文名、简称、代码搜索）
   */
  async searchCities(keyword: string): Promise<CityDTO[]> {
    if (!keyword || keyword.trim() === '') {
      return this.getHotCities()
    }
    const response: ApiResponse<CityDTO[]> = await this.instance.get('/v1/medusa/cities/search', {
      params: { q: keyword },
    })
    return this.extractData(response) || []
  }

  // ─── Phase 3.1 考勤系统对接 ──────────────────────────────────────────────

  /**
   * 查询申请人已占用时段（逻辑0：结束时间选择器置灰用）
   *
   * 失败时由调用方静默降级（catch 后 setOccupiedRanges([])）。
   */
  async getOccupiedPeriods(
    applicantId: string,
    excludeId?: string | number,
  ): Promise<OccupiedPeriod[]> {
    const params: Record<string, string | number> = { applicantId }
    if (excludeId !== undefined && excludeId !== null) {
      params.excludeId = excludeId
    }
    const response: ApiResponse<OccupiedPeriod[]> = await this.instance.get(
      '/v1/applications/occupied-periods',
      { params },
    )
    return this.extractData(response) || []
  }

  /**
   * 排班校验（逻辑1：开始时间选定后调用）
   */
  async checkScheduling(
    employeeId: string,
    date: string,
  ): Promise<ApiResponse<CheckSchedulingResponse>> {
    return this.instance.post('/v1/attendance/check-scheduling', { employeeId, date })
  }

  /**
   * 出差时长查询（逻辑2：开始/结束时间均选定后调用）
   */
  async calculateHours(
    req: CalculateHoursRequest,
  ): Promise<ApiResponse<CalculateHoursResponse>> {
    return this.instance.post('/v1/attendance/calculate-hours', req)
  }

  /**
   * 获取当前登录用户信息（Header 头像、姓名、部门）
   */
  async getCurrentUser(): Promise<{ userId: string; userName: string; departmentName: string; avatarUrl?: string | null; avatarInitial: string; displayNumber?: string; department?: string; collection?: string }> {
    const res = await this.instance.get('/v1/users/me')
    return res.data
  }

  /**
   * 按域账号精确查询单个员工
   */
  async getEmployeeByDomain(domain: string): Promise<EmployeeDTO> {
    const response: ApiResponse<EmployeeDTO> = await this.instance.get(`/v1/medusa/employees/by-domain/${domain}`)
    return this.extractData(response)
  }

  /**
   * 退出登录
   * 后端销毁 Session 并返回 CAS 服务端注销 URL（CAS 关闭时返回 "/"）
   */
  async logout(): Promise<{ logoutUrl: string }> {
    const res = await this.instance.post('/v1/users/logout')
    return res.data
  }
}

export const apiClient = new ApiClient()
